/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { FormEvent, useEffect, useState } from "react";
import PasswordPageMeta from "./PasswordPageMeta";
import passwordPageStyle from "./passwordPageStyle";
import Container from "../../../../components/Container/Container";
import Paper from "@mui/material/Paper";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import ButtonLoading from "../../../../components/Button/ButtonLoading";
import { login, logout } from "../../../../store/login/actions";
import * as messagesActions from "../../../../store/messages/actions";
import Layout from "../../../Layouts/Layout";
import { useLocation, useNavigate } from "react-router";
import LegalbirdLoader from "../../../../components/ContentLoader/LegalbirdLoader";
import ButtonTextTermsOfServicePrivacy from "../../../../components/Checkin/Common/ButtonTextTermsOfServicePrivacy/ButtonTextTermsOfServicePrivacy";
import _includes from "lodash/includes";
import Grid from "@mui/material/Grid";
import PartnerLogoSection from "../../../../components/Sections/Generic/PartnerLogoSection/PartnerLogoSection";
import Typography from "@mui/material/Typography";
import { Box, TextField } from "@mui/material";
import ApiClient, { apiGet, apiPatch } from "../../../../services/apiClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../../services/reactQuery/reactQueryService";
import { getPartnerIriByName } from "../../../../constants/partnerIris";
import { gaEvent } from "../../../../services/googleAnalyticsService";
import { Partner } from "../../../../types/Partner";

export default function PasswordPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [recoveryData, setRecoveryData] = useState({
    showAGB: false,
    partner: null,
    target: null,
  });

  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = new URLSearchParams(location.search);
  const id = query.get("id");
  const token = query.get("t");
  const email = query.get("email");
  const target = query.get("target");

  useEffect(() => {
    if (!id || !token) {
      navigate("/servicewelt");
      return;
    }

    logout();
    apiGet("/check_recovery", { id, t: token }).then(
      (recoveryResponse) => {
        if (!recoveryResponse.passwordRecoveryExists) {
          recoveryNotFoundMessage();
          navigate("/login", { state: { email: email } });
          return;
        }
        setRecoveryData(recoveryResponse);
        setIsChecking(false);
      },
      () => recoveryNotFoundMessage()
    );
  }, [id, token]);

  const partnerIri = getPartnerIriByName(recoveryData.partner);

  const partnerQueryKey = partnerIri ? [...queryKeys.all(partnerIri), "logo"] : ["partner", "missing-iri", "logo"];

  const { data: partnerEntity } = useQuery(
    {
      queryKey: partnerQueryKey,
      queryFn: async (): Promise<Partner | null> => {
        if (!partnerIri) {
          return null;
        }

        return (await ApiClient.get(partnerIri)) as Partner;
      },
      enabled: !!partnerIri,
    }
  );

  const recoveryNotFoundMessage = () => {
    messagesActions.addMessage({
      type: "warning",
      text: "Der Einmal-Link aus Ihrer E-Mail ist nicht mehr gültig.",
    });
  };

  const loginErrorsMessage = () => {
    messagesActions.addMessage({
      type: "warning",
      text: "Ihre Daten konnten nicht verifiziert werden. Sollte dieses Problem bestehen bleiben nutzen Sie bitte erneut den Link aus Ihrer E-Mail",
    });
    setIsLoading(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    if (!id || !token) {
      navigate("/servicewelt");
      return;
    }
    try {
      const pwSetResponse = await apiPatch(`/customers/${id}/set_password`, {
        plainPassword: password,
        token: token,
      });
      if (!pwSetResponse?.email) {
        loginErrorsMessage();
        return;
      }
      await login({ email: pwSetResponse.email, password });
      if (location.pathname.includes("/setzen")) {
        gaEvent({
          category: "Registration",
          action: "Account verified",
        });
      }
    } catch (e) {
      loginErrorsMessage();
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });

    if (target) {
      // ensure target is a relative link below servicewelt
      navigate(`/servicewelt/${target}`);
    } else {
      navigate(recoveryData.target || "/servicewelt");
    }
  };

  if (isChecking) {
    return (
      <Layout minimal={true}>
        <Container>
          <Container sx={{ width: 250 }}>
            <LegalbirdLoader />
          </Container>
        </Container>
      </Layout>
    );
  }

  const isPasswordRecovery = _includes(location.pathname, "/vergessen");

  return (
    <Layout backgroundType={"landingpage"} minimal={true}>
      <PasswordPageMeta />
      <Container>
        <Paper elevation={4} sx={passwordPageStyle.formContainer}>
          <Box component={"form"} sx={passwordPageStyle.form} onSubmit={handleSubmit}>
            {!isPasswordRecovery && (
              <Typography variant={"h3"} sx={passwordPageStyle.headline}>
                Vielen Dank für Ihre Bestätigung und herzlich willkommen!
              </Typography>
            )}
            <Typography variant={"h4"} sx={passwordPageStyle.title}>
              Jetzt ein {isPasswordRecovery && "neues"} Passwort für Ihre Servicewelt festlegen
            </Typography>
            {partnerIri && partnerEntity && (
              <Typography variant={"body1"} sx={passwordPageStyle.coopText}>
                Die <strong>{partnerEntity.brandName}</strong> hat uns bereits über Ihr Anliegen informiert und die <strong>Deckungszusage erteilt</strong>. Wir
                stehen bereit, um sofort für Sie tätig zu werden. Um Ihre digitale Fallakte zu schützen und die vertrauliche Kommunikation mit uns zu starten,
                legen Sie bitte jetzt Ihr Passwort fest.
              </Typography>
            )}
            <Grid container spacing={8}>
              <Grid
                size={{
                  md: 6,
                  xs: 12
                }}>
                <TextField
                  fullWidth={true}
                  type={showPassword ? "text" : "password"}
                  label="Passwort"
                  name="password"
                  value={password}
                  onChange={({ target }) => setPassword(target.value)}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  md: 6,
                  xs: 12
                }}>
                <ButtonLoading sx={passwordPageStyle.buttonMargin} color="primary" variant="contained" type="submit" fullWidth isLoading={isLoading}>
                  Zur Servicewelt
                </ButtonLoading>
                {recoveryData.showAGB && <ButtonTextTermsOfServicePrivacy />}
              </Grid>
            </Grid>
          </Box>
        </Paper>
        {!!getPartnerIriByName(recoveryData.partner) && <PartnerLogoSection partnerIri={getPartnerIriByName(recoveryData.partner)!} />}
      </Container>
    </Layout>
  );
}
